import type { KeyValueStore } from "./types.js";

/**
 * Visitor + session identity. The visitor_id is a long-lived (1y) cookie — the same browser is
 * the same visitor across sessions (design §Return Visitors). The session_id rolls over after
 * 30 min of inactivity (design §Session). Both are UUID v4. Pure functions over an injected
 * KeyValueStore so they unit-test without a browser.
 */
const VISITOR_KEY = "_ir_vid";
const SESSION_KEY = "_ir_sid";
const SESSION_TS_KEY = "_ir_sts";
const LANDING_KEY = "_ir_land";
const SEEN_KEY = "_ir_seen";
const VISITOR_TTL_DAYS = 365;
// Persistent (not session) cookie so the session survives page reloads and
// tab restores. The 30-min idle check still governs actual session expiry.
const SESSION_TTL_DAYS = 1;

export function uuid(): string {
  return crypto.randomUUID();
}

export interface VisitorState {
  visitorId: string;
  /** True when this browser was seen before (drives return_visit). */
  returning: boolean;
}

export function getOrCreateVisitor(store: KeyValueStore): VisitorState {
  const existing = store.get(VISITOR_KEY);
  if (existing) {
    store.set(VISITOR_KEY, existing, VISITOR_TTL_DAYS); // refresh TTL
    return { visitorId: existing, returning: store.get(SEEN_KEY) === "1" };
  }
  const visitorId = uuid();
  store.set(VISITOR_KEY, visitorId, VISITOR_TTL_DAYS);
  return { visitorId, returning: false };
}

export interface SessionState {
  sessionId: string;
  /** True if a NEW session was started on this call (drives session_start + landing_page). */
  isNew: boolean;
}

export function getOrCreateSession(
  store: KeyValueStore,
  nowMs: number,
  idleMs: number,
): SessionState {
  const sid = store.get(SESSION_KEY);
  const lastTs = Number(store.get(SESSION_TS_KEY) ?? 0);
  const fresh = !sid || nowMs - lastTs > idleMs;

  const sessionId = fresh ? uuid() : sid!;
  store.set(SESSION_KEY, sessionId, SESSION_TTL_DAYS);
  store.set(SESSION_TS_KEY, String(nowMs), SESSION_TTL_DAYS);
  return { sessionId, isNew: fresh };
}

/** Record that this browser has now been seen (so the next visit is a return_visit). */
export function markSeen(store: KeyValueStore): void {
  store.set(SEEN_KEY, "1", VISITOR_TTL_DAYS);
}

/** The first URL of the current session, used as attribution.landing_page. */
export function landingPage(store: KeyValueStore, currentUrl: string, isNewSession: boolean): string {
  if (isNewSession) {
    store.set(LANDING_KEY, currentUrl);
    return currentUrl;
  }
  return store.get(LANDING_KEY) ?? currentUrl;
}
